import { PartsSvg } from "@/components/Icon/Icon";
import { Thumbnail } from "@/components/Thumbnail/Thumbnail"; // Import Thumbnail component
import { CaseStatus } from "@/store/baseClientApi";
import { selectIsThumbnailCaching, selectLastFetched } from "@/store/slices/thumbnailsCacheSlice";
import { RootState } from "@/store/store";
import { ModelLabel } from "@/typed/ModelTypes";
import { getCaseThumbnailFromIndexedDB, Store } from "@/utils/indexeddb";
import { Box } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

interface CaseThumbnailProps {
  id: number;
  status: CaseStatus;
  label: ModelLabel;
  storeName: Store;
  size?: number;
}

export const CaseThumbnail: FC<CaseThumbnailProps> = ({ id, status, label, storeName, size = 50 }) => {
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [imageId, setImageId] = useState<number | undefined>(id);
  const [loading, setLoading] = useState<boolean>(true);

  const lastFetched = useSelector((state: RootState) => selectLastFetched(state, id.toString()));
  const isFetching = useSelector((state: RootState) => selectIsThumbnailCaching(state, id.toString()));

  const getThumbnail = useMemo(
    () => async (signal: AbortSignal) => {
      const cachedThumbnail = await getCaseThumbnailFromIndexedDB(id, status, label, storeName);
      if (signal.aborted) {
        return;
      }
      setImageUrl(cachedThumbnail);
      setLoading(false);
    },
    [id, status, label, storeName]
  );

  useEffect(() => {
    if (imageUrl === undefined) {
      setLoading(true);
      const controller = new AbortController();
      getThumbnail(controller.signal);
      return () => controller.abort();
    }
  }, []); //eslint-disable-line

  useEffect(() => {
    if (imageId !== id) {
      setLoading(true);
      setImageId(id);
      setImageUrl(undefined);
    }
  }, [id]); //eslint-disable-line

  useEffect(() => {
    if (lastFetched === undefined) {
      return;
    }
    if (imageUrl === undefined) {
      setLoading(true);
    }

    const controller = new AbortController();
    getThumbnail(controller.signal);
    return () => controller.abort();
  }, [lastFetched, getThumbnail]); //eslint-disable-line

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "50px",
        height: "50px",
        boxSizing: "border-box",
        flexShrink: 0
      }}
    >
      <Thumbnail
        imageUrl={imageUrl}
        loading={loading || (isFetching && imageUrl === undefined)}
        fallback={<PartsSvg width={size} height={size} opacity={0.3} />}
      />
    </Box>
  );
};
